import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useBranch } from "../context/BranchContext";
import { Input } from "../components/ui/input";
import {
  Search, Clock, Receipt, CalendarPlus, Phone, MessageCircle, Sparkles,
  User, Users, Layers, Scissors, Sparkle, Footprints, Droplets, Wind, Palette, FlaskConical,
} from "lucide-react";

const GOLD = "#C9A24B";
const ROSE = "#D8A38B";

const CATS = [
  { key: "all", label: "ALL", icon: Sparkles, match: () => true },
  { key: "men", label: "MEN", icon: User, match: (s) => s.gender === "male" || /\(men\)/i.test(s.name) },
  { key: "women", label: "WOMEN", icon: Users, match: (s) => s.gender === "female" || /\(women\)/i.test(s.name) },
  { key: "combos", label: "COMBOS", icon: Layers, match: (s) => /combo|package/i.test(s.category + s.name) },
  { key: "haircut", label: "HAIR CUT & STYLES", icon: Scissors, match: (s) => /hair ?cut|styl|blow|iron|tong/i.test(s.category + s.name) },
  { key: "facials", label: "FACIALS", icon: Sparkle, match: (s) => /facial|clean ?up|de-?tan/i.test(s.category + s.name) },
  { key: "pedimani", label: "PEDICURE & MANICURE", icon: Footprints, match: (s) => /pedicure|manicure/i.test(s.category + s.name) },
  { key: "waxing", label: "WAXING", icon: Droplets, match: (s) => /wax/i.test(s.category + s.name) },
  { key: "spa", label: "HAIR SPA", icon: Wind, match: (s) => /spa/i.test(s.category + s.name) },
  { key: "colour", label: "HAIR COLOUR", icon: Palette, match: (s) => /colou?r|highlight|global/i.test(s.category + s.name) },
  { key: "treat", label: "HAIR TREATMENTS", icon: FlaskConical, match: (s) => /keratin|treatment|smooth|botox|nano/i.test(s.category + s.name) },
];

export default function ServiceMenu() {
  const navigate = useNavigate();
  const { branchParam, selected, branches } = useBranch();
  const [services, setServices] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const id = "pf-font";
    if (!document.getElementById(id)) {
      const l = document.createElement("link");
      l.id = id; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,700;1,500&family=Cormorant+Garamond:wght@400;500&display=swap";
      document.head.appendChild(l);
    }
  }, []);

  useEffect(() => {
    api.get("/services", { params: { branch_id: branchParam } })
      .then((r) => setServices(r.data.filter((s) => s.status !== "inactive"))).catch(() => {});
  }, [branchParam]);

  const branch = branches.find((b) => b.id === selected);
  const priceOf = (s) => { const bp = s.branch_prices || {}; return (selected && bp[selected] != null) ? Number(bp[selected]) : Number(s.price || 0); };

  const list = useMemo(() => {
    const c = CATS.find((x) => x.key === cat);
    const term = q.trim().toLowerCase();
    return services.filter((s) => c.match(s) && (!term || (s.name || "").toLowerCase().includes(term)));
  }, [services, cat, q]);

  const addToBill = (s) => navigate("/billing/new", { state: { prefill: {
    branch_id: selected && selected !== "all" ? selected : (branches[0]?.id),
    services: [{ id: s.id, name: s.name, category: s.category, price: priceOf(s), actual_price: s.actual_price || priceOf(s), duration: s.duration, incentive_eligible: s.incentive_eligible !== false }],
  } } });

  const digits = (branch?.whatsapp || branch?.phone || "").replace(/\D/g, "");

  return (
    <div style={{ fontFamily: "'Cormorant Garamond', serif" }} className="min-h-screen -m-4 sm:-m-6 bg-[#0a0705] text-[#EFE6D8]"
      data-testid="service-menu">
      {/* subtle gold glow backdrop */}
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.15]" style={{ background: `radial-gradient(60% 50% at 50% 0%, ${GOLD}, transparent 70%)` }} />
        <header className="relative px-6 sm:px-10 pt-12 pb-8 text-center">
          <p className="tracking-[0.5em] text-[11px] mb-3" style={{ color: ROSE }}>ESTD · LUXURY SALON</p>
          <h1 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl sm:text-6xl font-bold tracking-wide">
            <span style={{ color: GOLD }}>DEFINE</span> SALON
          </h1>
          <div className="mx-auto mt-4 h-px w-40" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
          <p className="mt-4 text-lg italic text-[#C9BCA6]">Service Menu {branch ? `· ${branch.name}` : ""}</p>

          {/* search + contact */}
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-2xl mx-auto">
            <div className="relative w-full sm:max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: GOLD }} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services…" data-testid="menu-search"
                className="pl-9 bg-black/40 border-[#C9A24B]/30 text-[#EFE6D8] placeholder:text-[#8b7f6c] focus-visible:ring-[#C9A24B]/40 rounded-full h-11" />
            </div>
            {digits && (
              <div className="flex gap-2">
                <a href={`tel:${digits}`} data-testid="menu-call" className="h-11 w-11 grid place-items-center rounded-full border border-[#C9A24B]/40 hover:bg-[#C9A24B]/10 transition-colors"><Phone size={17} style={{ color: GOLD }} /></a>
                <a href={`https://wa.me/91${digits.slice(-10)}`} target="_blank" rel="noreferrer" data-testid="menu-whatsapp" className="h-11 w-11 grid place-items-center rounded-full border border-[#C9A24B]/40 hover:bg-[#C9A24B]/10 transition-colors"><MessageCircle size={17} style={{ color: GOLD }} /></a>
              </div>
            )}
          </div>
        </header>
      </div>

      {/* category nav */}
      <nav className="sticky top-0 z-10 bg-[#0a0705]/95 backdrop-blur border-y border-[#C9A24B]/15 px-4 py-3">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {CATS.map((c) => {
            const Icon = c.icon; const active = cat === c.key;
            return (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`menu-cat-${c.key}`}
                className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-[0.15em] transition-all duration-300"
                style={active ? { background: `linear-gradient(135deg, ${GOLD}, ${ROSE})`, color: "#1a1206" } : { border: "1px solid rgba(201,162,75,0.3)", color: "#C9BCA6" }}>
                <Icon size={14} /> {c.label}
              </button>
            );
          })}
        </div>
      </nav>

      {/* cards */}
      <div className="px-4 sm:px-10 py-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 max-w-7xl mx-auto">
        {list.map((s, i) => {
          const price = priceOf(s); const actual = Number(s.actual_price || 0);
          const hasOffer = actual > price;
          return (
            <div key={s.id} data-testid={`menu-service-${s.id}`}
              className="group relative rounded-2xl p-6 bg-gradient-to-b from-[#15100a] to-[#0d0a06] border border-[#C9A24B]/25 hover:border-[#C9A24B]/60 transition-all duration-500 hover:-translate-y-1"
              style={{ animation: `fadeUp .5s ease both`, animationDelay: `${(i % 9) * 60}ms`, boxShadow: "0 10px 40px -20px rgba(201,162,75,0.4)" }}>
              <div className="flex items-start justify-between gap-3">
                <h3 style={{ fontFamily: "'Playfair Display', serif" }} className="text-xl leading-snug pr-2">{s.name}</h3>
                <div className="text-right shrink-0">
                  {hasOffer && <div className="text-xs text-[#8b7f6c] line-through">₹{actual.toLocaleString("en-IN")}</div>}
                  <div className="text-2xl font-semibold" style={{ color: GOLD }}>₹{price.toLocaleString("en-IN")}</div>
                </div>
              </div>
              {s.description ? <p className="mt-2 text-[15px] text-[#B7AB95] leading-relaxed line-clamp-2">{s.description}</p>
                : s.sub_category ? <p className="mt-2 text-[15px] text-[#B7AB95] italic">{s.sub_category}</p> : null}
              <div className="mt-4 flex items-center gap-2 text-sm text-[#C9BCA6]"><Clock size={14} style={{ color: ROSE }} /> {s.duration} min</div>
              <div className="mt-5 h-px w-full" style={{ background: `linear-gradient(90deg, ${GOLD}22, transparent)` }} />
              <div className="mt-5 flex gap-2">
                <button onClick={() => addToBill(s)} data-testid={`menu-addbill-${s.id}`}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm tracking-wide text-[#1a1206] font-medium transition-transform hover:scale-[1.02]"
                  style={{ background: `linear-gradient(135deg, ${GOLD}, ${ROSE})` }}><Receipt size={15} /> Add to Bill</button>
                <button onClick={() => navigate("/appointments", { state: { prefillService: { id: s.id, name: s.name, duration: s.duration || 30, branch_id: (selected && selected !== "all") ? selected : (branches[0]?.id || "") } } })} data-testid={`menu-book-${s.id}`}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm tracking-wide border border-[#C9A24B]/40 text-[#EFE6D8] hover:bg-[#C9A24B]/10 transition-colors"><CalendarPlus size={15} style={{ color: GOLD }} /> Book Now</button>
              </div>
            </div>
          );
        })}
        {!list.length && <p className="col-span-full text-center text-[#8b7f6c] py-16 italic">No services found in this category.</p>}
      </div>

      <style>{`@keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}} .no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}
